import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { ApIcon } from '@alixpartners/ui-components';
import SplineColumnChart from '../Charts/SplineColumnChart';
import ServerSideDataSource from './ServerSideDataSource';
import { formatNumber, formatSpendWithDecimalAndSymbol } from 'utils/number';
import DeepDiveTable from './DeepDive/DeepDiveTable';
import { isUndefined } from 'lodash';
import { getDateFormat } from 'CategoryManagement/utils';
import Modal from 'CategoryManagement/CategoryWidget/Components/Modal/Modal';
import { getCommodityHierarchy } from 'utils/apiRequests';
import TimelineContent from './Timeline/TimelineContent';
import {
	IconWrapper,
	InsightOptionButton,
	TableWrapper,
} from './TableView.styles';
import { SettingsContext } from 'contexts/SettingsContext';

const formatSKUFields = skuObj => {
	if (Object.keys(skuObj).length > 0) {
		return {
			sku: skuObj.sKU,
			countCountryOrigin: skuObj.distinct_Count_of_Origin_Country,
			supplierCount: skuObj.distinct_Count_of_Supplier,
			lpp: skuObj.lPP,
			totalSpend: skuObj.sum_of_Total_Spend,
			totalVolume: skuObj.sum_of_Volume,
			weightedAvgPrice: skuObj.wt_Avg_Price,
		};
	} else {
		return {
			sku: '',
			countCountryOrigin: 0,
			supplierCount: 0,
			lpp: 0,
			totalSpend: 0,
			totalVolume: 0,
			weightedAvgPrice: 0,
		};
	}
};

const TableView = ({
	gridRef,
	content,
	filterData,
	skuCteFilterModel,
	isRenderingInitially,
	currencySymbol,
	distinctSku,
}) => {
	const gridStyle = useMemo(
		() => ({
			height: '40vh',
			width: '100%',
		}),
		[]
	);
	const { hideDecimals } = useContext(SettingsContext);
	const [visualView, setVisualView] = useState(false);
	const [isDeepDiveModal, setIsDeepDiveModal] = useState(false);
	const [isTimelineModal, setIsTimelineModal] = useState(false);
	const [skuDetails, setSkuDetails] = useState({});
	const [commodityHierarchy, setCommodityHierarchy] = useState({});
	const [columnDefs, setColumnDefs] = useState([
		{
			headerName: 'SKU',
			field: 'sKU',
			colId: 'sKU',
			sortable: false,
			filter: 'agSetColumnFilter',
			filterParams: {
				values: distinctSku,
			},
		},
		{
			headerName: 'SKU Description',
			field: 'sKU_Description',
			colId: 'sKU_Description',
			sortable: false,
			filter: false,
		},
		{
			headerName: 'Suppliers',
			field: 'distinct_Count_of_Supplier',
			colId: 'distinct_Count_of_Supplier',
			sortable: true,
			filter: false,
		},
		{
			headerName: 'Countries',
			field: 'distinct_Count_of_Origin_Country',
			colId: 'distinct_Count_of_Origin_Country',
			sortable: true,
			filter: false,
		},
		{
			headerName: 'Purchase Amount',
			field: 'sum_of_Total_Spend',
			colId: 'sum_of_Total_Spend',
			sortable: true,
			filter: false,
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return formatSpendWithDecimalAndSymbol(
						params.value,
						currencySymbol,
						hideDecimals,
						1
					).toUpperCase();
				}
			},
		},
		{
			headerName: 'PO Wt.Avg Unit Price',
			field: 'wt_Avg_Price',
			colId: 'wt_Avg_Price',
			sortable: true,
			filter: false,
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return formatSpendWithDecimalAndSymbol(
						params.value,
						currencySymbol,
						hideDecimals,
						1
					).toUpperCase();
				}
			},
		},
		{
			headerName: 'Last Price Paid (LPP)',
			field: 'lPP',
			colId: 'lPP',
			sortable: true,
			filter: false,
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return formatSpendWithDecimalAndSymbol(
						params.value,
						currencySymbol,
						hideDecimals,
						1
					).toUpperCase();
				}
			},
		},
		{
			headerName: 'Volume',
			field: 'sum_of_Volume',
			colId: 'sum_of_Volume',
			sortable: true,
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return formatNumber(params.value);
				}
			},
		},
		{
			headerName: 'SKU Spend Summary',
			field: 'visual',
			sortable: false,
			filter: false,
			cellRendererFramework: params => (
				<InsightOptionButton
					onClick={() => {
						setSkuDetails(formatSKUFields(params.data));
						setVisualView(true);
					}}
				>
					<IconWrapper>
						<ApIcon material={true} iconName="bar_chart" />
					</IconWrapper>
				</InsightOptionButton>
			),
		},
		{
			headerName: 'SKU Price Progression',
			field: 'timeline',
			sortable: false,
			filter: false,
			cellRendererFramework: params => (
				<InsightOptionButton
					onClick={() => {
						setSkuDetails(formatSKUFields(params.data));
						setIsTimelineModal(true);
					}}
				>
					<IconWrapper>
						<ApIcon material={true} iconName="stacked_line_chart" />
					</IconWrapper>
				</InsightOptionButton>
			),
		},
		{
			headerName: 'Data Table',
			field: 'deep-dive',
			sortable: false,
			filter: false,
			cellRendererFramework: params => (
				<InsightOptionButton
					onClick={() => {
						setSkuDetails(formatSKUFields(params.data));
						setIsDeepDiveModal(true);
					}}
				>
					<IconWrapper>
						<ApIcon material={true} iconName="table_view" />
					</IconWrapper>
				</InsightOptionButton>
			),
		},
	]);
	useEffect(
		() => {
			setColumnDefs(prev => {
				const newColumnDefs = [...prev]; // Create a copy of the previous state
				newColumnDefs[0] = {
					...newColumnDefs[0],
					filterParams: {
						values: distinctSku,
					},
				};
				return newColumnDefs; // Return the new state
			});
		},
		[distinctSku]
	);
	const defaultColDefs = useMemo(() => {
		return {
			flex: 1,
			minWidth: 100,
			resizable: true,
			editable: false,
		};
	}, []);

	const gridOptions = () => {
		return {
			modules: AllModules,
			columnDefs: columnDefs,
			cacheBlockSize: 200,
			maxBlocksInCache: 6,
			enableColResize: true,
			enableSorting: true,
			enableFilter: true,
			rowHeight: 35,
			rowModelType: 'serverSide',
			maxConcurrentDatasourceRequests: 1,
			multiSortKey: 'ctrl',
			enableRangeSelection: true,
			suppressCutToClipboard: true,
		};
	};

	const onGridReady = useCallback(
		params => {
			const { dateFrom, dateTo } = getDateFormat(
				filterData.dateRange.stateRange
			);
			gridRef.current.api.startEditingCell(params);
			const gridOptionsnew = {
				columnDefs,
				suppressRowClickSelection: true,
				...gridOptions(),
				skuCteFilterModel: skuCteFilterModel,
				dateRange: { dateFrom, dateTo },
			};
			const datasource = ServerSideDataSource(
				gridOptionsnew,
				params.columnApi,
				params.api
			);
			params.api.setServerSideDatasource(datasource);
		}, // eslint-disable-next-line
		[columnDefs, gridOptions]
	);

	useEffect(
		() => {
			if (isRenderingInitially !== 1) {
				if (Object.keys(skuCteFilterModel).length > 0) {
					const { dateFrom, dateTo } = getDateFormat(
						filterData.dateRange.stateRange
					);
					const gridOptionsnew = {
						columnDefs,
						suppressRowClickSelection: true,
						...gridOptions(),
						skuCteFilterModel: skuCteFilterModel,
						dateRange: { dateFrom, dateTo },
					};
					const datasource = ServerSideDataSource(
						gridOptionsnew,
						gridRef.current.columnApi,
						gridRef.current.api
					);
					gridRef.current.api.setServerSideDatasource(datasource);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isRenderingInitially, skuCteFilterModel]
	);

	useEffect(() => {
		getCommodityHierarchy().then(hierarchyResponse => {
			if (hierarchyResponse.succeeded) {
				if (hierarchyResponse.data !== null) {
					if (Object.keys(hierarchyResponse.data).length > 0) {
						setCommodityHierarchy(hierarchyResponse.data);
					}
				}
			}
		});
	}, []);

	return (
		<>
			<TableWrapper>
				<div style={gridStyle} className="ag-theme-balham">
					<AgGridReact
						{...gridOptions()}
						ref={gridRef}
						modules={AllModules}
						columnDefs={columnDefs}
						cacheBlockSize={200}
						defaultColDef={defaultColDefs}
						maxBlocksInCache={6}
						enableColResize={true}
						enableSorting={true}
						enableFilter={true}
						enableGroupEdit={true}
						rowModelType="serverSide"
						maxConcurrentDatasourceRequests={1}
						multiSortKey="ctrl"
						onGridReady={onGridReady}
						enableRangeSelection={true}
						suppressCutToClipboard={true}
						suppressCellFocus={true}
					/>
				</div>
			</TableWrapper>
			{visualView &&
				skuDetails.sku !== '' && (
					<Modal
						title=""
						isFullScreen={false}
						width="90%"
						height="90%"
						zIndex={2}
						closeLabel="Close"
						onClose={() => setVisualView(false)}
					>
						<SplineColumnChart
							modelId={content}
							skuDetails={skuDetails}
							filterData={filterData}
							currencySymbol={currencySymbol}
							hideDecimals={hideDecimals}
						/>
					</Modal>
				)}

			{isDeepDiveModal && (
				<Modal
					title=""
					isFullScreen={false}
					width="90%"
					height="70%"
					zIndex={2}
					closeLabel="Close"
					onClose={() => setIsDeepDiveModal(false)}
				>
					<DeepDiveTable
						modelId={content}
						filterData={filterData}
						skuDetails={skuDetails}
						currencySymbol={currencySymbol}
						hideDecimals={hideDecimals}
					/>
				</Modal>
			)}

			{isTimelineModal && (
				<Modal
					title=""
					isFullScreen={false}
					width="90%"
					height="90%"
					zIndex={2}
					closeLabel="Close"
					onClose={() => setIsTimelineModal(false)}
				>
					<TimelineContent
						modelId={content}
						filterData={filterData}
						skuDetails={skuDetails}
						commodityHierarchy={commodityHierarchy}
						currencySymbol={currencySymbol}
						hideDecimals={hideDecimals}
					/>
				</Modal>
			)}
		</>
	);
};

export default TableView;
